// admin/program/components/ProgramForm.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Timestamp } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaUsers, 
  FaTag, 
  FaImage,
  FaUserTie,
  FaFileAlt,
  FaCheckCircle,
  FaSave,
  FaYoutube,
  FaSlidersH,
  FaDesktop,
  FaMobileAlt
} from 'react-icons/fa';
import { programService, Program } from '../../../firebase/programFirestoreService';
import { uploadFile } from '../../../firebase/storageService';
import { useUI } from '../../../components/common/UIMessageProvider';

interface ProgramFormData {
  title: string;
  description: string;
  content: string;
  youtubeUrl: string;
  category: string;
  startDate: string;
  endDate: string;
  location: string;
  organizer: string;
  capacity: number;
  imageUrl: string;
  tags: string;
  requiresRegistration: boolean;
  requireGhanaCard?: boolean;
  requireDateOfBirth?: boolean;
  showInCarousel?: boolean;
  showInCards?: boolean;
}

const ProgramForm: React.FC = () => {
  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    reset,
    watch 
  } = useForm<ProgramFormData>({
    defaultValues: {
      requiresRegistration: false,
      requireGhanaCard: false,
      requireDateOfBirth: false,
      showInCarousel: false,
      showInCards: false
    }
  });
  
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [newsCardImageFile, setNewsCardImageFile] = useState<File | null>(null);
  const [newsCardImagePreviewUrl, setNewsCardImagePreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [otherImagesUrls, setOtherImagesUrls] = useState<string[]>([]);
  const [otherPendingFiles, setOtherPendingFiles] = useState<File[]>([]);
  const [otherPendingPreviews, setOtherPendingPreviews] = useState<string[]>([]);
  const [youtubePreview, setYoutubePreview] = useState<string>('');
  const [newsCardImagePositionX, setNewsCardImagePositionX] = useState<number>(50);
  const [newsCardImagePositionY, setNewsCardImagePositionY] = useState<number>(50);
  const [existingProgram, setExistingProgram] = useState<Program | null>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ui = useUI();
  const HOME_CAROUSEL_IMAGE_SIZE = '1560 x 525';

  const requiresRegistration = watch('requiresRegistration', false);
  const requireGhanaCard = watch('requireGhanaCard', false);
  const requireDateOfBirth = watch('requireDateOfBirth', false);
  const youtubeUrlValue = watch('youtubeUrl', '');

  const extractYoutubeId = (url: string): string => {
    if (!url) return '';
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7] && match[7].length === 11) ? match[7] : '';
  };

  const isValidYoutubeUrl = (url: string): boolean => {
    if (!url.trim()) return true;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    if (!youtubeRegex.test(url)) return false;
    return extractYoutubeId(url).length === 11;
  };

  const getYoutubeEmbedUrl = (url: string): string => {
    const videoId = extractYoutubeId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  useEffect(() => {
    if (youtubeUrlValue && isValidYoutubeUrl(youtubeUrlValue)) {
      setYoutubePreview(getYoutubeEmbedUrl(youtubeUrlValue));
    } else {
      setYoutubePreview('');
    }
  }, [youtubeUrlValue]);

const onSubmit = async (data: ProgramFormData) => {
  setLoading(true);
  try {
    if (data.youtubeUrl && !isValidYoutubeUrl(data.youtubeUrl)) {
      ui.showMessage('Please enter a valid YouTube URL', { type: 'error' });
      setLoading(false);
      return;
    }

    let imageUrl = data.imageUrl || '';
    if (imageFile) {
      const res = await uploadFile(imageFile, 'program-images');
      if (!res.success || !res.url) {
        throw new Error(res.error || 'Failed to upload program image');
      }
      imageUrl = res.url;
    }

    let newsCardImageUrl = '';
    if (newsCardImageFile) {
      // Reuse the same allowed path as main program images to avoid storage-rule mismatches.
      const cardRes = await uploadFile(newsCardImageFile, 'program-images');
      if (!cardRes.success || !cardRes.url) {
        throw new Error(cardRes.error || 'Failed to upload card image');
      }
      newsCardImageUrl = cardRes.url;
    } else if (newsCardImagePreviewUrl && /^https?:\/\//.test(newsCardImagePreviewUrl)) {
      newsCardImageUrl = newsCardImagePreviewUrl;
    }
    // Upload any pending other images and merge with existing URLs
    const uploadedOtherUrls: string[] = [];
    const failedOtherUploads: string[] = [];
    if (otherPendingFiles.length > 0) {
      for (const f of otherPendingFiles) {
        try {
          const r = await uploadFile(f, 'program-images');
          if (r.success && r.url) {
            uploadedOtherUrls.push(r.url);
          } else {
            failedOtherUploads.push(`${f.name}: ${r.error || 'upload failed'}`);
          }
        } catch (e) {
          failedOtherUploads.push(f.name);
          console.warn('Failed to upload other image', e);
        }
      }
    }
    
    // Extract time from datetime-local inputs
    const startDateTime = new Date(data.startDate);
    const endDateTime = new Date(data.endDate);
    
    const programData = {
      ...data,
      youtubeUrl: data.youtubeUrl?.trim() || '',
      startDate: Timestamp.fromDate(startDateTime),
      endDate: Timestamp.fromDate(endDateTime),
      startTime: startDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      endTime: endDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      registrationDeadline: null,
      registeredCount: existingProgram?.registeredCount || 0,
      isPublished: existingProgram?.isPublished || false,
      status: existingProgram?.status || ('draft' as const),
      tags: (data.tags || '').split(',').map(tag => tag.trim()).filter(Boolean),
      capacity: parseInt((data.capacity || 0).toString()) || 0,
      isDeleted: existingProgram?.isDeleted ?? 0,
      requiresRegistration: !!data.requiresRegistration,
      showInCarousel: !!data.showInCarousel,
      showInCards: !!data.showInCards,
      newsCardImagePositionX: Math.max(0, Math.min(100, Number(newsCardImagePositionX) || 50)),
      newsCardImagePositionY: Math.max(0, Math.min(100, Number(newsCardImagePositionY) || 50)),
      requireGhanaCard: !!data.requireGhanaCard,
      requireDateOfBirth: !!data.requireDateOfBirth,
      isFeatured: existingProgram?.isFeatured || false
    };

    if (imageUrl) {
      (programData as any).imageUrl = imageUrl;
    }
    (programData as any).newsCardImageUrl = newsCardImageUrl || '';

    // Attach otherImages if present
    const finalOtherImages = [...otherImagesUrls, ...uploadedOtherUrls];
    if (finalOtherImages.length > 0) (programData as any).otherImages = finalOtherImages;

    if (id) {
      const res = await programService.updateProgram(id, programData as any);
      if (res && (res as any).success) {
        ui.showMessage(
          failedOtherUploads.length > 0
            ? `Program updated, but ${failedOtherUploads.length} additional image${failedOtherUploads.length === 1 ? '' : 's'} failed to upload.`
            : 'Program updated successfully!',
          { type: failedOtherUploads.length > 0 ? 'warning' : 'success' }
        );
        navigate('/admin/programs');
      } else {
        throw (res as any).error || new Error('Update failed');
      }
    } else {
      const programId = await programService.createProgram(programData as any);
      if (programId) {
        ui.showMessage(
          failedOtherUploads.length > 0
            ? `Program created, but ${failedOtherUploads.length} additional image${failedOtherUploads.length === 1 ? '' : 's'} failed to upload.`
            : 'Program created successfully!',
          { type: failedOtherUploads.length > 0 ? 'warning' : 'success' }
        );
        navigate('/admin/programs');
      } else {
        throw new Error('Create failed');
      }
    }
  } catch (error) {
    console.error('Error creating/updating program:', error);
    ui.showMessage('Failed to save program', { type: 'error' });
  } finally {
    setLoading(false);
  }
};



  useEffect(() => {
    const load = async () => {
      if (!id) {
        setExistingProgram(null);
        setNewsCardImagePositionX(50);
        setNewsCardImagePositionY(50);
        setNewsCardImageFile(null);
        setNewsCardImagePreviewUrl(null);
        return;
      }
      setLoading(true);
      try {
        const p = await programService.getProgram(id);
        if (p) {
          setExistingProgram(p);
          reset({
            title: p.title || '',
            description: p.description || '',
            content: p.content || '',
            youtubeUrl: p.youtubeUrl || '',
            category: p.category || '',
            startDate: p.startDate ? new Date(p.startDate.toDate()).toISOString().slice(0,16) : '',
            endDate: p.endDate ? new Date(p.endDate.toDate()).toISOString().slice(0,16) : '',
            location: p.location || '',
            organizer: p.organizer || '',
            capacity: p.capacity || 0,
            imageUrl: p.imageUrl || '',
            // other images are managed separately
            tags: (p.tags || []).join(', '),
            requiresRegistration: !!p.requiresRegistration,
            requireGhanaCard: !!p.requireGhanaCard,
            requireDateOfBirth: !!p.requireDateOfBirth,
            showInCarousel: !!p.showInCarousel,
            showInCards: !!p.showInCards
          });
          if (p.imageUrl) setPreviewUrl(p.imageUrl);
          setNewsCardImagePreviewUrl((p as any).newsCardImageUrl || null);
          setNewsCardImageFile(null);
          if (p.youtubeUrl && isValidYoutubeUrl(p.youtubeUrl)) {
            setYoutubePreview(getYoutubeEmbedUrl(p.youtubeUrl));
          } else {
            setYoutubePreview('');
          }
          if ((p as any).otherImages && Array.isArray((p as any).otherImages)) {
            setOtherImagesUrls((p as any).otherImages as string[]);
          }
          setNewsCardImagePositionX(typeof (p as any).newsCardImagePositionX === 'number' ? (p as any).newsCardImagePositionX : 50);
          setNewsCardImagePositionY(typeof (p as any).newsCardImagePositionY === 'number' ? (p as any).newsCardImagePositionY : 50);
        }
      } catch (err) {
        console.error('Error loading program for edit', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, reset]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      ui.showMessage('File size must be less than 5MB', { type: 'error' });
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      ui.showMessage('Please upload an image file', { type: 'error' });
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleNewsCardImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      ui.showMessage('File size must be less than 5MB', { type: 'error' });
      return;
    }

    if (!file.type.startsWith('image/')) {
      ui.showMessage('Please upload an image file', { type: 'error' });
      return;
    }

    setNewsCardImageFile(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setNewsCardImagePreviewUrl(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleOtherImagesUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const valid: File[] = [];
    const previews: string[] = [];
    let skippedForType = 0;
    let skippedForSize = 0;

    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        skippedForType += 1;
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        skippedForSize += 1;
        continue;
      }
      valid.push(file);
      previews.push(URL.createObjectURL(file));
    }

    const skippedCount = skippedForType + skippedForSize;
    if (skippedCount > 0) {
      const reasons = [
        skippedForSize ? `${skippedForSize} over 5MB` : '',
        skippedForType ? `${skippedForType} not image files` : ''
      ].filter(Boolean).join(', ');
      ui.showMessage(`${skippedCount} additional image${skippedCount === 1 ? '' : 's'} skipped (${reasons}).`, { type: 'warning' });
    }

    if (valid.length === 0) return;
    setOtherPendingFiles(prev => [...prev, ...valid]);
    setOtherPendingPreviews(prev => [...prev, ...previews]);
  };

  const removeExistingOtherImage = (url: string) => {
    setOtherImagesUrls(prev => prev.filter(u => u !== url));
  };

  const removePendingOtherImage = (index: number) => {
    setOtherPendingFiles(prev => prev.filter((_, i) => i !== index));
    setOtherPendingPreviews(prev => {
      const removed = prev[index];
      try { URL.revokeObjectURL(removed); } catch (e) {}
      return prev.filter((_, i) => i !== index);
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-6xl mx-auto"
    >
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          {id ? 'Edit Program' : 'Create New Program'}
        </h1>
        <p className="text-gray-600 mt-1">
          {id ? 'Update program details below' : 'Fill in the details to create a new program'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          {/* Form Sections */}
          <div className="p-6 space-y-8">
            {/* Basic Information */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...register('requireGhanaCard')}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Require Ghana Card</div>
                    <div className="text-xs text-gray-500">If enabled, registrants must provide Ghana Card number.</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    {...register('requireDateOfBirth')}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-700">Require Date of Birth</div>
                    <div className="text-xs text-gray-500">If enabled, registrants must provide their date of birth.</div>
                  </div>
                </div>
              </div>

              {/* Conditional preview of registration fields for this program */}
              {requiresRegistration && (requireGhanaCard || requireDateOfBirth) && (
                <div className="mt-4 bg-gray-50 border border-dashed border-gray-200 rounded p-4">
                  <div className="text-sm font-semibold text-gray-800 mb-2">Registration Form Preview</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {requireGhanaCard && (
                      <div className="space-y-1">
                        <label className="text-xs text-gray-600">Ghana Card Number (will appear on registration)</label>
                        <input type="text" disabled className="w-full px-3 py-2 border border-gray-200 rounded bg-white/50" placeholder="Ghana Card Number" />
                      </div>
                    )}

                    {requireDateOfBirth && (
                      <div className="space-y-1">
                        <label className="text-xs text-gray-600">Date of Birth (will appear on registration)</label>
                        <input type="date" disabled className="w-full px-3 py-2 border border-gray-200 rounded bg-white/50" />
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Title */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaFileAlt className="text-primary-500" />
                    Program Title *
                  </label>
                  <input
                    type="text"
                    {...register('title', { required: 'Title is required' })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Enter program title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm">{errors.title.message}</p>
                  )}
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaTag className="text-primary-500" />
                    Category
                  </label>
                  <select
                    {...register('category')}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white"
                  >
                    <option value="">Select Category</option>
                    <option value="Workshop">Workshop</option>
                    <option value="Seminar">Seminar</option>
                    <option value="Training">Training</option>
                    <option value="Conference">Conference</option>
                    <option value="Outreach">Outreach</option>
                    <option value="Scholarship">Scholarship</option>
                    <option value="internship">Internship</option>
                  </select>
                </div>

                {/* Description */}
                <div className="md:col-span-2 space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaFileAlt className="text-primary-500" />
                    Description *
                  </label>
                  <textarea
                    rows={3}
                    {...register('description', { required: 'Description is required' })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Brief description of the program"
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm">{errors.description.message}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Date & Time */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-secondary-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">Date & Time</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Start Date */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaCalendarAlt className="text-secondary-500" />
                    Start Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    {...register('startDate', { required: 'Start date is required' })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors"
                  />
                  {errors.startDate && (
                    <p className="text-red-500 text-sm">{errors.startDate.message}</p>
                  )}
                </div>

                {/* End Date */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaCalendarAlt className="text-secondary-500" />
                    End Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    {...register('endDate', { required: 'End date is required' })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary-500 focus:border-secondary-500 transition-colors"
                  />
                  {errors.endDate && (
                    <p className="text-red-500 text-sm">{errors.endDate.message}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Location & Organizer */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-accent-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">Location & Organizer</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Location */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaMapMarkerAlt className="text-accent-500" />
                    Location *
                  </label>
                  <input
                    type="text"
                    {...register('location', { required: 'Location is required' })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                    placeholder="Venue or online platform"
                  />
                  {errors.location && (
                    <p className="text-red-500 text-sm">{errors.location.message}</p>
                  )}
                </div>

                {/* Organizer */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaUserTie className="text-accent-500" />
                    Organizer *
                  </label>
                  <input
                    type="text"
                    {...register('organizer', { required: 'Organizer is required' })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-accent-500 transition-colors"
                    placeholder="Program organizer"
                  />
                  {errors.organizer && (
                    <p className="text-red-500 text-sm">{errors.organizer.message}</p>
                  )}
                </div>
              </div>
            </section>

            {/* Capacity & Tags */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">Additional Details</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Capacity */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaUsers className="text-primary-500" />
                    Capacity
                  </label>
                  <input
                    type="number"
                    {...register('capacity')}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Leave blank for unlimited"
                    min="0"
                  />
                  <p className="text-xs text-gray-500">
                    Enter 0 or leave blank for unlimited participants
                  </p>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaTag className="text-primary-500" />
                    Tags
                  </label>
                  <input
                    type="text"
                    {...register('tags')}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="education, workshop, skill-development"
                  />
                  <p className="text-xs text-gray-500">
                    Separate tags with commas
                  </p>
                </div>
              </div>
            </section>

            {/* YouTube Video */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-red-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">YouTube Video (Optional)</h2>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <FaYoutube className="text-red-600" />
                    YouTube URL
                  </label>
                  <input
                    type="text"
                    {...register('youtubeUrl')}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                  {youtubeUrlValue && !isValidYoutubeUrl(youtubeUrlValue) && (
                    <p className="text-sm text-red-600">
                      Please enter a valid YouTube URL (youtube.com or youtu.be)
                    </p>
                  )}
                </div>

                {youtubePreview && (
                  <div className="bg-white rounded-lg p-3 border border-red-100">
                    <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-lg">
                      <iframe
                        src={youtubePreview}
                        title="YouTube video preview"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute top-0 left-0 w-full h-full"
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Homepage Visibility */}
            <section>
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-4 border border-primary-200">
                <h3 className="font-semibold text-primary-800 mb-3 flex items-center gap-2">
                  <FaSlidersH className="text-primary-700" />
                  Homepage Visibility
                </h3>
                <p className="text-sm text-primary-600 mb-4">
                  Choose whether this program should appear in homepage sections after it is published.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg">
                          <FaDesktop className="text-cyan-600" />
                        </div>
                        <span className="font-medium text-gray-800">Homepage Carousel</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          {...register('showInCarousel')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-cyan-500 peer-checked:to-blue-500"></div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Shows this program in the main homepage carousel, even after the program end date.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-gradient-to-br from-violet-100 to-purple-100 rounded-lg">
                          <FaMobileAlt className="text-violet-600" />
                        </div>
                        <span className="font-medium text-gray-800">Homepage Cards</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          {...register('showInCards')}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-violet-500 peer-checked:to-purple-500"></div>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Shows this program in the homepage activity cards, even after the program end date.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Image Upload */}
            <section>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1.5 h-6 bg-secondary-600 rounded-full"></div>
                <h2 className="text-lg font-semibold text-gray-900">Program Image</h2>
              </div>
              
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary-400 transition-colors">
                  <div className="flex flex-col items-center">
                    <FaImage className="text-3xl text-gray-400 mb-3" />
                    <p className="text-sm text-gray-600 mb-2">
                      Drag & drop an image here, or click to browse
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      Max size: 5MB. Homepage carousel target: {HOME_CAROUSEL_IMAGE_SIZE}. News cards target: 1:1 square.
                    </p>
                    <label className="cursor-pointer">
                      <div className="px-4 py-2 bg-primary-50 text-primary-700 hover:bg-primary-100 font-medium rounded-lg transition-colors">
                        Choose Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                      </div>
                    </label>
                  </div>
                </div>

                {previewUrl && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-4"
                  >
                    <div className="relative group rounded-lg overflow-hidden">
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full max-h-80 object-contain rounded-lg border border-gray-200 bg-white"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewUrl(null);
                          setImageFile(null);
                        }}
                        className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Remove
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-xl border border-gray-200 p-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">
                          Homepage Carousel Preview ({HOME_CAROUSEL_IMAGE_SIZE})
                        </p>
                        <div
                          className="w-full rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                          style={{ aspectRatio: '1560 / 525' }}
                        >
                          <img
                            src={previewUrl}
                            alt="Carousel preview"
                            className="w-full h-full object-cover object-center"
                          />
                        </div>
                      </div>

                      <div className="bg-white rounded-xl border border-gray-200 p-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">
                          Homepage News Card Preview (Square 1:1)
                        </p>
                        <div className="w-full max-w-[260px] rounded-lg overflow-hidden border border-gray-200 bg-gray-100 aspect-square">
                          <img
                            src={newsCardImagePreviewUrl || previewUrl}
                            alt="Card preview"
                            className="w-full h-full object-cover"
                            style={{ objectPosition: `${newsCardImagePositionX}% ${newsCardImagePositionY}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-sm font-semibold text-gray-800 mb-3">
                        News Card Crop Position (Only affects homepage news cards)
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Horizontal Focus</span>
                            <span>{Math.round(newsCardImagePositionX)}%</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={newsCardImagePositionX}
                            onChange={(e) => setNewsCardImagePositionX(Number(e.target.value))}
                            className="w-full accent-primary-600"
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span>Vertical Focus</span>
                            <span>{Math.round(newsCardImagePositionY)}%</span>
                          </div>
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={newsCardImagePositionY}
                            onChange={(e) => setNewsCardImagePositionY(Number(e.target.value))}
                            className="w-full accent-primary-600"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <p className="text-sm font-semibold text-gray-800 mb-2">
                        Card-Specific Image (Optional)
                      </p>
                      <p className="text-xs text-gray-600 mb-3">
                        If set, homepage news cards use this image for this program. Program details keep the main image.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="cursor-pointer">
                          <div className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm">
                            Upload Card Image
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleNewsCardImageUpload}
                              className="hidden"
                            />
                          </div>
                        </label>
                        {newsCardImagePreviewUrl && (
                          <button
                            type="button"
                            onClick={() => {
                              setNewsCardImageFile(null);
                              setNewsCardImagePreviewUrl(null);
                            }}
                            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                          >
                            Remove Card Image
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Other Images */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Images</label>
                  <div className="mb-2 text-xs text-gray-500">You can add multiple images to appear in the program gallery. Max size per image: 5MB.</div>
                  <div className="flex items-center gap-3">
                    <label className="cursor-pointer">
                      <div className="px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                        Add Images
                        <input type="file" accept="image/*" multiple onChange={handleOtherImagesUpload} className="hidden" />
                      </div>
                    </label>
                    <div className="text-sm text-gray-500">{otherImagesUrls.length} saved • {otherPendingFiles.length} new</div>
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-3">
                    {otherImagesUrls.map((url) => (
                      <div key={url} className="relative rounded overflow-hidden">
                        <img src={url} alt="other" className="w-full h-24 object-cover" />
                        <button type="button" onClick={() => removeExistingOtherImage(url)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full text-xs">×</button>
                      </div>
                    ))}

                    {otherPendingPreviews.map((src, idx) => (
                      <div key={src} className="relative rounded overflow-hidden">
                        <img src={src} alt={`pending-${idx}`} className="w-full h-24 object-cover" />
                        <button type="button" onClick={() => removePendingOtherImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full text-xs">×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="space-y-2">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary-500 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-600 text-center">
                      Uploading... {uploadProgress}%
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Registration & Content */}
            <section>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Registration Toggle */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1.5 h-6 bg-accent-600 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900">Registration</h3>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <FaCheckCircle className={requiresRegistration ? "text-accent-500" : "text-gray-400"} />
                      <div>
                        <p className="font-medium text-gray-900">Requires Registration</p>
                        <p className="text-sm text-gray-500">
                          Participants need to register to join
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('requiresRegistration')}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-500"></div>
                    </label>
                  </div>
                </div>

                {/* Detailed Content */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-1.5 h-6 bg-primary-600 rounded-full"></div>
                    <h3 className="text-lg font-semibold text-gray-900">Detailed Content</h3>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Program Details & Agenda
                    </label>
                    <textarea
                      rows={5}
                      {...register('content')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Enter detailed program content, agenda, requirements, etc."
                    />
                    <p className="text-xs text-gray-500">
                      This will be displayed on the program details page
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Footer with Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div>
                <p className="text-sm text-gray-600">
                  Fields marked with * are required
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/programs')}
                  className="px-6 py-2.5 border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium rounded-lg transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      {id ? 'Updating...' : 'Creating...'}
                    </>
                  ) : (
                    <>
                      <FaSave className="text-sm" />
                      {id ? 'Update Program' : 'Create Program'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Quick Tips */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-medium text-blue-900 mb-2">💡 Quick Tips</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Use clear, descriptive titles for better discoverability</li>
          <li>• Set realistic capacity limits based on venue/resources</li>
          <li>• Include relevant tags for better filtering</li>
          <li>• For homepage use, prepare images for 1560 x 525 (carousel) and 1:1 (news cards)</li>
          <li>• Detailed content helps participants understand what to expect</li>
        </ul>
      </div>
    </motion.div>
  );
};

export default ProgramForm;
